import { listIntegrationsOptions } from "@/api/generated/@tanstack/react-query.gen";
import { type PermissionProps } from "@/components/misc";
import {
  Sidebar,
  SidebarContent,
  // SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
} from "@/components/ui/sidebar";
import { type FileRoutesByTo } from "@/routeTree.gen";
import { useOrganization, useUser } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ActivityIcon,
  BotIcon,
  BoxIcon,
  ChartLineIcon,
  CheckCircle2Icon,
  DatabaseIcon,
  FigmaIcon,
  FileEditIcon,
  FingerprintIcon,
  FlaskConicalIcon,
  KeyIcon,
  KeyRoundIcon,
  LayersIcon,
  LayoutDashboardIcon,
  LayoutTemplateIcon,
  type LucideIcon,
  PaletteIcon,
  PlugIcon,
  ScaleIcon,
  TableIcon,
  VariableIcon,
  WorkflowIcon,
} from "lucide-react";
import * as React from "react";
import { CollapsibleSidebarLink } from "./collapsible-sidebar-link";
import { NeonBlueLogo } from "./neon-blue-logo";
import { OrgSwitcher } from "./org-switcher";
import { SidebarLink } from "./sidebar-link/sidebar-link";

export type SubLinkProps = {
  to: keyof FileRoutesByTo;
  icon: LucideIcon;
  label: string;
  iconColor?: string;
} & PermissionProps;

export type CustomLinkProps = {
  to: keyof FileRoutesByTo;
  icon: LucideIcon;
  label: string;
  badge?: string;
  activeTo?: ReadonlyArray<keyof FileRoutesByTo>;
  subLinks?: Array<SubLinkProps>;
  separator?: boolean;
} & PermissionProps;

export const AppSidebar: React.FC = () => {
  const { isLoaded: isUserLoaded, user } = useUser();
  const { isLoaded: isOrgLoaded, organization } = useOrganization();
  const showLinks = isUserLoaded
    && isOrgLoaded
    && (user?.organizationMemberships.length ?? 0) > 0
    && organization !== null;

  const integrationsQuery = useQuery({
    ...listIntegrationsOptions(),
    enabled: showLinks,
  });
  const hasDynamicImage = (integrationsQuery.data?.items ?? []).some(
    (i) => i.type === "dynamic_image",
  );

  const links = React.useRef<Array<CustomLinkProps>>([
    {
      icon: LayoutDashboardIcon,
      label: "Dashboard",
      to: "/",
    },
    {
      icon: FlaskConicalIcon,
      label: "Experiments",
      to: "/experiments",
      requires: "org:experiment_v2:experiment_v2_read",
    },
    {
      icon: FileEditIcon,
      label: "Draft Experiments",
      to: "/draft-experiments",
      requires: "org:draft_experiments:read",
    },
    {
      icon: LayersIcon,
      label: "Content",
      to: "/content",
      subLinks: [
        { to: "/content/styles", icon: PaletteIcon, label: "Styles" },
        { to: "/content/rules", icon: ScaleIcon, label: "Rules" },
        { to: "/content/variables", icon: VariableIcon, label: "Variables" },
        { to: "/content/evaluation", icon: CheckCircle2Icon, label: "Evaluation" },
        { to: "/content/templates", icon: LayoutTemplateIcon, label: "Templates" },
        { to: "/content/schemas", icon: DatabaseIcon, label: "Schemas" },
        // { to: "/content/evaluator", icon: PlayIcon, label: "Evaluator" },
      ],
    },
    {
      icon: BoxIcon,
      label: "Warehouse",
      to: "/warehouse",
      requires: "org:warehouse:warehouse_read",
      subLinks: [
        { to: "/warehouse/overview", icon: WorkflowIcon, label: "Overview" },
        { to: "/warehouse/id-types", icon: KeyIcon, label: "ID Types" },
        { to: "/warehouse/source-tables", icon: TableIcon, label: "Source Tables" },
        { to: "/warehouse/features", icon: FingerprintIcon, label: "Features" },
        { to: "/warehouse/metrics", icon: ChartLineIcon, label: "Metrics" },
      ],
    },
  ]);

  const bottomLinks = React.useMemo<Array<CustomLinkProps>>(() => {
    const items: Array<CustomLinkProps> = [];
    if (hasDynamicImage) {
      items.push({
        icon: FigmaIcon,
        label: "Figma",
        to: "/figma",
        requires: "org:integrations_v2:read",
      });
    }
    items.push(
      {
        icon: DatabaseIcon,
        label: "Source Templates",
        to: "/source-templates",
        requires: "org:source_templates:read",
      },
      {
        icon: PlugIcon,
        label: "Integrations",
        to: "/integrations",
        requires: "org:integrations_v2:read",
      },
      {
        icon: BotIcon,
        label: "Agents",
        to: "/agents",
        requires: "org:integrations_v2:read",
      },
      {
        icon: ActivityIcon,
        label: "Jobs",
        to: "/jobs",
        requires: "org:jobs:read",
      },
      {
        icon: KeyRoundIcon,
        label: "API Keys",
        to: "/api-keys",
      },
    );
    return items;
  }, [hasDynamicImage]);

  return (
    <Sidebar collapsible="icon" className={"border-r border-border/50"}>
      <HeaderWithToggle />

      <SidebarContent className="group-data-[collapsible=icon]:overflow-visible">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {showLinks
                && links.current.map((linkProps) => (
                  <React.Fragment key={linkProps.to}>
                    {linkProps.subLinks != null
                      ? <CollapsibleSidebarLink {...linkProps} />
                      : <SidebarLink {...linkProps} />}
                    {linkProps.separator && <div className="mx-2 my-2 border-b border-border" />}
                  </React.Fragment>
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mx-2 mt-auto border-b border-border" />
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {showLinks
                && bottomLinks.map((linkProps) => (
                  <SidebarLink
                    key={linkProps.to}
                    {...linkProps}
                  />
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

const HeaderWithToggle: React.FC = () => {
  return (
    <SidebarHeader className="border-b border-border/50">
      <Link to="/">
        <NeonBlueLogo />
      </Link>

      <div className="border-b border-border/50" />

      <div className="flex justify-start w-full self-center">
        <OrgSwitcher />
      </div>
    </SidebarHeader>
  );
};
